import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { AppFooter } from "@/components/AppFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Stethoscope, ArrowLeft, ShieldCheck } from "lucide-react";

export default function DietitianLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      const target = res.user.role === "super_admin" ? "/admin" : "/dashboard";
      navigate(target, { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="dietitian-login-page">
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full">
          <Logo size="lg" />
          <div className="mt-10 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary font-semibold">
            <Stethoscope size={14} />
            Dietitian portal
          </div>
          <h1 className="text-4xl font-bold tracking-tight mt-2">Sign in</h1>
          <p className="text-muted-foreground mt-2">Access your practice dashboard.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="dietitian-login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-email-input" className="h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" testId="login-password-input" className="h-12" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full h-12 text-base font-semibold rounded-full" data-testid="login-submit-button">
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-patient-login">
            <ArrowLeft size={14} /> Patient login
          </Link>
          <Link to="/admin/login" className="mt-3 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="link-super-admin-login">
            <ShieldCheck size={14} /> Super admin login
          </Link>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
